import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Crown, UserCheck, ChevronRight, ChevronLeft, Upload, Check, FileText, Eye, EyeOff } from 'lucide-react';
import { MAUZAS } from '../data/mock';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const Register = () => {
  const { registerNokma, registerApplicant } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('applicant');

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 py-12 px-4 tribal-pattern">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl font-bold">Create your account</h1>
            <p className="text-muted-foreground mt-2">Choose how you would like to register</p>
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full max-w-md mx-auto h-12">
              <TabsTrigger value="applicant" className="gap-2"><UserCheck className="h-4 w-4" /> Applicant</TabsTrigger>
              <TabsTrigger value="nokma" className="gap-2"><Crown className="h-4 w-4" /> Nokma Sani</TabsTrigger>
            </TabsList>
            <TabsContent value="applicant" className="mt-6">
              <ApplicantRegister registerApplicant={registerApplicant} navigate={navigate} />
            </TabsContent>
            <TabsContent value="nokma" className="mt-6">
              <NokmaRegister registerNokma={registerNokma} navigate={navigate} />
            </TabsContent>
          </Tabs>
          <div className="mt-8 text-center text-sm text-muted-foreground">
            Already registered? <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const NokmaRegister = ({ registerNokma, navigate }) => {
  const [data, setData] = useState({
    mobile: '', password: '', nokmaName: '', saniName: '', nokmaNumber: '',
    mauzaNumber: '', laskerArea: '', akhing: '',
  });
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const [k, v] of Object.entries(data)) {
      if (!v) { toast.error(`Please fill: ${k}`); return; }
    }
    if (!/^\d{10}$/.test(data.mobile)) { toast.error('Enter valid 10-digit mobile'); return; }
    if (data.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    const res = await registerNokma(data);
    if (!res.ok) { toast.error(res.error); return; }
    toast.success('Registration submitted. Awaiting Admin approval.');
    navigate('/login');
  };

  return (
    <Card className="border-border shadow-warm">
      <CardHeader>
        <CardTitle className="font-serif text-2xl">Nokma Sani Registration</CardTitle>
        <CardDescription>Visible only to the Administrator. Approval required before sign-in.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <div><Label>Nokma Name</Label><Input value={data.nokmaName} onChange={(e) => set('nokmaName', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Sani Name</Label><Input value={data.saniName} onChange={(e) => set('saniName', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Nokma Number</Label><Input value={data.nokmaNumber} onChange={(e) => set('nokmaNumber', e.target.value)} placeholder="e.g. NK-101" className="mt-1.5" /></div>
          <div>
            <Label>Mauza Number</Label>
            <Select value={data.mauzaNumber} onValueChange={(v) => set('mauzaNumber', v)}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select Mauza" /></SelectTrigger>
              <SelectContent>{MAUZAS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Lasker Area</Label><Input value={data.laskerArea} onChange={(e) => set('laskerArea', e.target.value)} className="mt-1.5" /></div>
          <div><Label>A&rsquo;khing (Village)</Label><Input value={data.akhing} onChange={(e) => set('akhing', e.target.value)} placeholder="e.g. Rongram A&rsquo;khing" className="mt-1.5" /></div>
          <div><Label>Mobile Number</Label><Input inputMode="numeric" maxLength={10} value={data.mobile} onChange={(e) => set('mobile', e.target.value.replace(/\D/g, ''))} className="mt-1.5" /></div>
          <PasswordField label="Password" value={data.password} onChange={(v) => set('password', v)} />
          <div className="sm:col-span-2 mt-2">
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90">Submit registration</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const STEPS = [
  { id: 1, label: 'Personal & ID' },
  { id: 2, label: 'Addresses' },
  { id: 3, label: 'Work Details' },
  { id: 4, label: 'Documents' },
];

const ApplicantRegister = ({ registerApplicant, navigate }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    // Step 1
    name: '', fatherName: '', mobile: '', password: '',
    aadharNumber: '', voterIdNumber: '',
    // Step 2
    applicantAddress: '', applicantPS: '', applicantState: '',
    presentAddress: '', presentPS: '', presentState: '',
    // Step 3
    natureOfBusiness: 'Trader', placeOfWork: '',
    responsiblePersonName: '', responsiblePersonAddress: '', responsiblePersonPhone: '',
    // Step 4
    docs: { aadhar: '', voter: '', photo: '' },
  });
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setDoc = (k, v) => setData((d) => ({ ...d, docs: { ...d.docs, [k]: v } }));

  const onFile = async (k, file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error('File must be < 4MB'); return; }
    const b64 = await fileToBase64(file);
    setDoc(k, b64);
    toast.success(`${k.toUpperCase()} uploaded`);
  };

  const validateStep1 = () => {
    if (!data.name || !data.fatherName || !data.mobile || !data.password ||
        !data.aadharNumber || !data.voterIdNumber) {
      toast.error('Please fill all fields'); return false;
    }
    if (!/^\d{10}$/.test(data.mobile)) { toast.error('Phone must be 10 digits'); return false; }
    if (data.password.length < 6) { toast.error('Password must be at least 6 characters'); return false; }
    if (!/^\d{12}$/.test(data.aadharNumber)) { toast.error('Aadhar must be 12 digits'); return false; }
    return true;
  };

  const validateStep2 = () => {
    for (const k of ['applicantAddress','applicantPS','applicantState','presentAddress','presentPS','presentState']) {
      if (!data[k]) { toast.error('Please fill all address fields'); return false; }
    }
    return true;
  };

  const validateStep3 = () => {
    if (!data.natureOfBusiness || !data.placeOfWork ||
        !data.responsiblePersonName || !data.responsiblePersonAddress ||
        !data.responsiblePersonPhone) {
      toast.error('Please fill all work fields'); return false;
    }
    if (!/^\d{10}$/.test(data.responsiblePersonPhone)) {
      toast.error('Responsible Person phone must be 10 digits'); return false;
    }
    return true;
  };

  const next = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    setStep((s) => Math.min(s + 1, 4));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    if (!data.docs.aadhar || !data.docs.voter || !data.docs.photo) {
      toast.error('Please upload all required documents'); return;
    }
    setSubmitting(true);
    const res = await registerApplicant(data);
    setSubmitting(false);
    if (!res.ok) { toast.error(res.error); return; }
    toast.success('Registration successful');
    navigate('/applicant');
  };

  return (
    <Card className="border-border shadow-warm">
      <CardHeader>
        <CardTitle className="font-serif text-2xl">Applicant Registration</CardTitle>
        <CardDescription>Step {step} of 4 — {STEPS[step - 1].label}</CardDescription>
        <div className="flex gap-2 mt-2">
          {STEPS.map((s) => (
            <div key={s.id} className={`h-1 flex-1 rounded ${step >= s.id ? 'bg-primary' : 'bg-muted'}`} />
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {step === 1 && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="1. Applicant's Name" value={data.name} onChange={(v) => set('name', v)} />
            <Field label="2. Father's Name" value={data.fatherName} onChange={(v) => set('fatherName', v)} />
            <Field label="3. Phone / WhatsApp (login)" value={data.mobile} onChange={(v) => set('mobile', v.replace(/\D/g, ''))} maxLength={10} inputMode="numeric" />
            <PasswordField label="Password (min 6 chars)" value={data.password} onChange={(v) => set('password', v)} />
            <Field label="10a. Aadhar Number (12 digits)" value={data.aadharNumber} onChange={(v) => set('aadharNumber', v.replace(/\D/g, ''))} maxLength={12} inputMode="numeric" />
            <Field label="10b. Election ID Card Number" value={data.voterIdNumber} onChange={(v) => set('voterIdNumber', v.toUpperCase())} />
            <div className="sm:col-span-2">
              <Button onClick={next} className="w-full bg-primary hover:bg-primary/90 gap-2">Continue <ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div>
              <div className="font-serif font-semibold mb-2">4. Applicant's Address</div>
              <Textarea value={data.applicantAddress} onChange={(e) => set('applicantAddress', e.target.value)} rows={2} placeholder="Address" />
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <Field label="P.S." value={data.applicantPS} onChange={(v) => set('applicantPS', v)} />
                <Field label="State" value={data.applicantState} onChange={(v) => set('applicantState', v)} />
              </div>
            </div>
            <div>
              <div className="font-serif font-semibold mb-2">5. Present Address</div>
              <Textarea value={data.presentAddress} onChange={(e) => set('presentAddress', e.target.value)} rows={2} placeholder="Present Address" />
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <Field label="P.S." value={data.presentPS} onChange={(v) => set('presentPS', v)} />
                <Field label="State" value={data.presentState} onChange={(v) => set('presentState', v)} />
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={back} className="gap-2"><ChevronLeft className="h-4 w-4" /> Back</Button>
              <Button onClick={next} className="flex-1 bg-primary hover:bg-primary/90 gap-2">Continue <ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div>
              <div className="font-serif font-semibold mb-2">6. Nature of Business</div>
              <RadioGroup value={data.natureOfBusiness} onValueChange={(v) => set('natureOfBusiness', v)} className="grid grid-cols-3 gap-2">
                {['Trader', 'Labour', 'Hawker'].map((p) => (
                  <label key={p} className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer ${data.natureOfBusiness === p ? 'border-primary bg-primary/5' : 'border-border'}`}>
                    <RadioGroupItem value={p} />
                    <span className="text-sm font-medium">{p}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label>9. Place of Work / Trade</Label>
              <Textarea value={data.placeOfWork} onChange={(e) => set('placeOfWork', e.target.value)} rows={2} className="mt-1.5" placeholder="Specify the exact location/market/area" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Responsible Person's Name" value={data.responsiblePersonName} onChange={(v) => set('responsiblePersonName', v)} />
              <Field label="8. Responsible Person's Phone" value={data.responsiblePersonPhone} onChange={(v) => set('responsiblePersonPhone', v.replace(/\D/g, ''))} maxLength={10} inputMode="numeric" />
            </div>
            <div>
              <Label>7. Responsible Person's Address</Label>
              <Textarea value={data.responsiblePersonAddress} onChange={(e) => set('responsiblePersonAddress', e.target.value)} rows={2} className="mt-1.5" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={back} className="gap-2"><ChevronLeft className="h-4 w-4" /> Back</Button>
              <Button onClick={next} className="flex-1 bg-primary hover:bg-primary/90 gap-2">Continue <ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">Upload clear photos/scans of the documents below.</div>
            {[
              { k: 'aadhar', label: 'Aadhar Card' },
              { k: 'voter', label: 'Election Identity Card' },
              { k: 'photo', label: 'Self Photo' },
            ].map((d) => (
              <div key={d.k} className="border border-dashed border-border rounded-lg p-4 flex items-center gap-4">
                <div className="h-12 w-12 rounded-md bg-secondary flex items-center justify-center">
                  {data.docs[d.k] ? <Check className="h-5 w-5 text-accent" /> : <FileText className="h-5 w-5 text-muted-foreground" />}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">{d.label}</div>
                  <div className="text-xs text-muted-foreground">{data.docs[d.k] ? 'Uploaded' : 'JPG / PNG / PDF up to 4MB'}</div>
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onFile(d.k, e.target.files?.[0])} />
                  <span className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm hover:bg-primary/90">
                    <Upload className="h-4 w-4" /> Upload
                  </span>
                </label>
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={back} className="gap-2"><ChevronLeft className="h-4 w-4" /> Back</Button>
              <Button onClick={submit} disabled={submitting} className="flex-1 bg-primary hover:bg-primary/90">
                {submitting ? 'Registering...' : 'Complete registration'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Field = ({ label, value, onChange, maxLength, inputMode }) => (
  <div>
    <Label>{label}</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} maxLength={maxLength} inputMode={inputMode} className="mt-1.5" />
  </div>
);

const PasswordField = ({ label, value, onChange }) => {
  const [show, setShow] = React.useState(false);
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative mt-1.5">
        <Input type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} className="pr-10" />
        <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide' : 'Show'} className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

export default Register;
